import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import BackToAdminButton from "../components/BackToAdminButton";
import MujahezPickQueue from "../components/mujahez/MujahezPickQueue";
import {
  Package,
  LogOut,
  Truck,
  CheckCircle,
  Clock,
  MapPin,
  Phone,
  ClipboardList,
  Eye,
  BellRing,
  History,
  IdCard,
  Store,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import JumlatyLogo from "../components/JumlatyLogo";
import InvoiceModal from "../components/InvoiceModal";
import { alarmNotify, adminVoice } from "../utils/voice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Mujahez = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invoiceOrder, setInvoiceOrder] = useState(null); // viewOnly = true for كشف
  const [driverPickFor, setDriverPickFor] = useState(null);
  const [newAlert, setNewAlert] = useState(null); // { invoice_number, user_name }
  const prevIdsRef = useRef(new Set());

  const fetchAll = async () => {
    let meRes, ordersRes, drvRes;
    try {
      [meRes, ordersRes, drvRes] = await Promise.all([
        axios.get(`${API}/mujahez/me`),
        axios.get(`${API}/mujahez/orders`),
        axios.get(`${API}/mujahez/drivers`),
      ]);
    } catch (err) {
      setLoading(false);
      return;
    }
    const newOrders = ordersRes.data || [];
    // Detect freshly assigned orders → siren + on-screen alert banner.
    const prev = prevIdsRef.current;
    const truly_new = newOrders.filter((o) => !prev.has(o.id) && prev.size > 0);
    prevIdsRef.current = new Set(newOrders.map((o) => o.id));
    setStats(meRes.data);
    setOrders(newOrders);
    setDrivers(drvRes.data || []);
    setLoading(false);
    if (truly_new.length > 0) {
      const first = truly_new[0];
      alarmNotify("جاءتك فاتورة جاهزة. " + (first.user_name || ""));
      setNewAlert({
        id: first.id,
        invoice_number: first.invoice_number || first.id.slice(0, 6).toUpperCase(),
        user_name: first.user_name,
        market_name: first.market_name,
        customer_id: first.customer_id,
      });
      try { toast.success(`📦 جاءتك فاتورة جديدة #${first.invoice_number || first.id.slice(0, 6)}`, { duration: 7000 }); } catch { /* ignore */ }
      setTimeout(() => setNewAlert(null), 12000);
    }
  };

  const fetchHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/mujahez/orders/all`, { params: { limit: 500 } });
      setHistory(data || []);
    } catch (err) {
      toast.error("فشل تحميل سجل الفواتير");
    }
  };

  useEffect(() => {
    if (!user) { navigate("/login"); return undefined; }
    if (user.role !== "mujahez" && user.role !== "admin") {
      navigate(user.role === "driver" ? "/sawakeen-jumlaty" : "/");
      return undefined;
    }
    // Defer the first fetch via microtask so React doesn't see setState
    // happening synchronously inside the effect (satisfies eslint rule
    // react-hooks/set-state-in-effect while preserving behavior).
    queueMicrotask(() => { fetchAll(); });
    const id = setInterval(() => { fetchAll(); }, 12000);
    return () => clearInterval(id);
  }, [user, navigate]);

  // Pre-load history once so toggling is instant.
  useEffect(() => {
    if (user?.role !== "mujahez" && user?.role !== "admin") return undefined;
    queueMicrotask(() => { fetchHistory(); });
    const id = setInterval(() => { fetchHistory(); }, 30000);
    return () => clearInterval(id);
  }, [user?.role]);

  const startPreparing = async (orderId) => {
    try {
      await axios.patch(`${API}/mujahez/orders/${orderId}/start`);
      toast.success("✓ تم تأكيد الطلب — بدأ التجهيز");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "حدث خطأ");
    }
  };

  const markReady = async (orderId, driverId) => {
    try {
      await axios.patch(`${API}/mujahez/orders/${orderId}/ready`, { driver_id: driverId });
      toast.success("تم التجهيز وإرسال الطلب للسائق");
      setDriverPickFor(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "حدث خطأ");
    }
  };

  const handleLogout = () => { logout(); navigate("/login"); };

  if (loading || !stats) {
    return <div className="min-h-screen flex items-center justify-center text-[#5C6E64]">جاري التحميل...</div>;
  }

  const statusLabel = {
    mujahez_assigned: { text: "بانتظار التأكيد", color: "#F5A623" },
    mujahez_preparing: { text: "قيد التجهيز", color: "#3B82F6" },
    ready_for_driver: { text: "جاهز — بانتظار السائق", color: "#7ED321" },
    assigned: { text: "مع السائق", color: "#9F7AEA" },
    accepted: { text: "في الطريق", color: "#9F7AEA" },
    on_the_way: { text: "في الطريق", color: "#9F7AEA" },
    awaiting_payment_confirmation: { text: "بانتظار تأكيد الأموال", color: "#A855F7" },
    delivered: { text: "تم التسليم", color: "#15803d" },
    rejected: { text: "ملغى", color: "#E85D45" },
  };

  return (
    <div className="min-h-screen bg-transparent">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-[#E4EBE6]">
        <div className="max-w-md mx-auto md:max-w-3xl px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex flex-col items-start">
            <JumlatyLogo size="sm" showTagline={false} />
            <span className="text-xs text-[#7ED321] font-bold mt-1">قسم التجهيز</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/mujahez-jumlaty/inventory")}
              data-testid="mj-orders-to-inventory"
              title="جرد المخزن"
              className="bg-[#0EA5E9] hover:bg-[#0284c7] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1"
            >
              <ClipboardList className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">جرد</span>
            </button>
            {stats.photo_path && (
              <img src={`${API}/files/${stats.photo_path}`} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-[#7ED321]" />
            )}
            <button onClick={handleLogout} data-testid="mujahez-logout" className="p-2 hover:bg-[#F3F6F4] rounded-full">
              <LogOut className="w-5 h-5 text-[#2D4A3E]" />
            </button>
          </div>
        </div>
      </header>

      {/* New-order alert banner — pops up when a fresh invoice is assigned */}
      <AnimatePresence>
        {newAlert && (
          <motion.button
            type="button"
            onClick={() => setNewAlert(null)}
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            data-testid="mujahez-new-alert"
            className="block w-full bg-gradient-to-l from-[#E85D45] to-[#F5A623] text-white py-3 px-4 shadow-lg relative overflow-hidden text-right"
          >
            <div className="max-w-3xl mx-auto flex items-center gap-3">
              <motion.div
                animate={{ rotate: [0, -15, 15, -15, 15, 0] }}
                transition={{ duration: 0.8, repeat: 3 }}
              >
                <BellRing className="w-7 h-7 shrink-0" />
              </motion.div>
              <div className="flex-1 min-w-0">
                <p className="font-black text-base">📦 جاءتك فاتورة جاهزة!</p>
                <p className="text-xs opacity-95 truncate">
                  #{newAlert.invoice_number} · {newAlert.user_name}
                  {newAlert.market_name && ` · ${newAlert.market_name}`}
                </p>
              </div>
              <span className="text-[10px] bg-white/20 rounded-full px-2 py-1 shrink-0">انقر للإغلاق</span>
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      <main className="max-w-md mx-auto md:max-w-3xl px-4 py-6 space-y-4">
        {/* NEW (Feb 17, 2026): admin-dispatched pick-list queue. Mujahez
            sees invoices sent from Admin's confirmed-orders tab with
            print buttons — no debts, no financials, just pick + print. */}
        <MujahezPickQueue />
        {/* Greeting + Stats */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-l from-[#7ED321] to-[#5DAE0F] text-white rounded-2xl p-4">
          <p className="text-xs opacity-90">أهلاً بك</p>
          <p className="font-bold text-xl">{stats.full_name}</p>
        </motion.div>

        <div className="grid grid-cols-4 gap-2" data-testid="mujahez-stats">
          <StatCard label="بانتظار" value={stats.pending} color="#F5A623" />
          <StatCard label="قيد التجهيز" value={stats.preparing} color="#3B82F6" />
          <StatCard label="جاهزة" value={stats.ready} color="#7ED321" />
          <StatCard label="اليوم" value={stats.today_done} color="#1E3A5F" />
        </div>

        {/* Active Orders */}
        <h2 className="font-heading font-bold text-lg text-[#111814] mt-4 flex items-center gap-2">
          <Package className="w-5 h-5 text-[#7ED321]" />
          قوائم التجهيز ({orders.length})
        </h2>

        {orders.length === 0 ? (
          <div className="bg-white border border-[#E4EBE6] rounded-2xl p-8 text-center">
            <Package className="w-16 h-16 text-[#E4EBE6] mx-auto mb-3" />
            <p className="text-[#5C6E64]">لا توجد قوائم للتجهيز حالياً</p>
          </div>
        ) : (
          orders.map((order, idx) => (
            <OrderCard
              key={order.id}
              order={order}
              idx={idx}
              statusLabel={statusLabel}
              onView={() => setInvoiceOrder(order)}
              onConfirm={() => startPreparing(order.id)}
              onReady={() => setDriverPickFor(order)}
            />
          ))
        )}

        {/* History toggle */}
        <button
          type="button"
          onClick={() => setShowHistory((v) => !v)}
          data-testid="mujahez-toggle-history"
          className="w-full mt-6 bg-[#1E3A5F] hover:bg-[#152a45] text-white font-bold rounded-2xl py-3 flex items-center justify-center gap-2"
        >
          <History className="w-4 h-4" />
          {showHistory ? "إخفاء سجل الفواتير" : `سجل الفواتير (${history.length})`}
        </button>

        {showHistory && (
          <div className="space-y-3" data-testid="mujahez-history-list">
            {history.length === 0 ? (
              <div className="bg-white border border-[#E4EBE6] rounded-2xl p-6 text-center text-xs text-[#5C6E64]">
                لا يوجد فواتير سابقة بعد.
              </div>
            ) : (
              history.map((order, idx) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  idx={idx}
                  statusLabel={statusLabel}
                  onView={() => setInvoiceOrder(order)}
                  compact
                />
              ))
            )}
          </div>
        )}
      </main>

      {/* Driver picker modal */}
      {driverPickFor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4" data-testid="driver-picker-modal">
          <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-3xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
            <h3 className="font-heading font-bold text-xl text-[#111814] mb-4 flex items-center gap-2">
              <Truck className="w-5 h-5 text-[#3B82F6]" />اختر السائق
            </h3>
            {drivers.length === 0 ? (
              <p className="text-[#5C6E64] text-sm">لا يوجد سائقون متاحون.</p>
            ) : (
              <div className="space-y-2">
                {drivers.map((d) => (
                  <button key={d.id} onClick={() => markReady(driverPickFor.id, d.id)} data-testid={`pick-driver-${d.id}`} className="w-full bg-[#F0F9FF] hover:bg-[#DBEAFE] border border-[#BAE6FD] rounded-xl p-3 flex items-center gap-3 text-right">
                    {d.photo_path ? (
                      <img src={`${API}/files/${d.photo_path}`} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-[#3B82F6]" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-white border-2 border-[#3B82F6] flex items-center justify-center"><Truck className="w-4 h-4 text-[#3B82F6]" /></div>
                    )}
                    <div className="flex-1">
                      <p className="font-bold text-[#111814]">{d.full_name}</p>
                      <p className="text-xs text-[#5C6E64]">{d.vehicle_type} {d.vehicle_number && `· ${d.vehicle_number}`}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => setDriverPickFor(null)} data-testid="picker-cancel" className="w-full mt-4 bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2.5 rounded-full">
              إلغاء
            </button>
          </motion.div>
        </div>
      )}

      {invoiceOrder && (
        <InvoiceModal
          order={invoiceOrder}
          customerInfo={{ customer_debt: invoiceOrder.amount_remaining || invoiceOrder.total }}
          onClose={() => setInvoiceOrder(null)}
          viewOnly
        />
      )}
      <BackToAdminButton />
    </div>
  );
};

// ─── Order Card sub-component ───
// Used for BOTH active orders (with actions) and history (compact, view-only).
const OrderCard = ({ order, idx, statusLabel, onView, onConfirm, onReady, compact = false }) => {
  const lbl = statusLabel[order.status];
  const invoiceLabel = `#${order.invoice_number || order.id.slice(0, 6).toUpperCase()}`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.03 }}
      data-testid={`mujahez-order-${order.id}`}
      className="bg-white border border-[#E4EBE6] rounded-2xl p-4 space-y-3 relative overflow-hidden"
    >
      {/* Orange invoice-number badge — top-right (RTL = right) */}
      <div
        className="absolute top-0 right-0 bg-gradient-to-l from-[#F5A623] to-[#E85D45] text-white px-4 py-2 rounded-bl-2xl shadow-md"
        data-testid={`mujahez-invoice-badge-${order.id}`}
      >
        <p className="text-[9px] opacity-90 leading-tight">رقم الفاتورة</p>
        <p className="font-black text-base leading-tight" dir="ltr">{invoiceLabel}</p>
      </div>

      <div className="pt-12">
        {/* Status pill */}
        {lbl && (
          <span className="inline-block text-[10px] font-bold px-3 py-1 rounded-full mb-2" style={{ backgroundColor: `${lbl.color}25`, color: lbl.color }}>
            {lbl.text}
          </span>
        )}

        {/* Customer block */}
        <div className="bg-[#F0FDF4] rounded-xl p-3 text-sm space-y-1">
          <p className="font-black text-[#111814] text-base">{order.user_name}</p>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
            {order.market_name && (
              <span className="flex items-center gap-1 text-[#1E3A5F]">
                <Store className="w-3 h-3" /> <b>{order.market_name}</b>
              </span>
            )}
            {order.customer_id && (
              <span className="flex items-center gap-1 text-[#7C3AED]">
                <IdCard className="w-3 h-3" /> رقم الزبون: <b dir="ltr">{order.customer_id}</b>
              </span>
            )}
            <a href={`tel:${order.user_phone}`} className="flex items-center gap-1 text-[#E85D45] hover:underline" dir="ltr">
              <Phone className="w-3 h-3" /> {order.user_phone}
            </a>
          </div>
          {order.address && (
            <p className="text-xs text-[#5C6E64] flex items-center gap-1 mt-1">
              <MapPin className="w-3 h-3" />{order.address}
            </p>
          )}
        </div>

        {/* Items count + total preview */}
        <div className="flex justify-between items-center mt-3 text-xs">
          <span className="text-[#5C6E64]">
            <Package className="w-3 h-3 inline" /> {order.items?.length || 0} ماده · {order.total_quantity || 0} قطعة
          </span>
          <span className="font-black text-[#15803d]" dir="ltr">
            {Number(order.total || 0).toLocaleString("en-US")} د.ع
          </span>
        </div>

        {/* Actions */}
        <div className="flex gap-2 flex-wrap mt-3">
          {/* View invoice (كشف) — always available */}
          <button
            onClick={onView}
            data-testid={`mujahez-view-${order.id}`}
            className="bg-[#1E3A5F] hover:bg-[#152a45] text-white font-bold py-2 px-4 rounded-full text-sm flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />كشف الفاتورة
          </button>

          {!compact && order.status === "mujahez_assigned" && (
            <button
              onClick={onConfirm}
              data-testid={`mujahez-confirm-${order.id}`}
              className="flex-1 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-bold py-2 rounded-full text-sm flex items-center justify-center gap-2"
            >
              <Clock className="w-4 h-4" />✓ تأكيد الطلب
            </button>
          )}
          {!compact && order.status === "mujahez_preparing" && (
            <button
              onClick={onReady}
              data-testid={`mujahez-ready-${order.id}`}
              className="flex-1 bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold py-2 rounded-full text-sm flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />تم التجهيز وتعيين سائق
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div className="bg-white border border-[#E4EBE6] rounded-xl p-2 text-center">
    <p className="font-bold text-2xl" style={{ color }}>{value}</p>
    <p className="text-[10px] text-[#5C6E64]">{label}</p>
  </div>
);

export default Mujahez;
